import { FormGroup } from '@angular/forms';

export interface MapPinLocation {
  lat: number;
  lng: number;
}

/** Every shape a coordinate reaches this module in: a number from a DTO, the raw
 *  value of a form control (a string while the admin is typing, '' while unset),
 *  or nothing at all. Narrower than `any`, so a caller handing over an object or
 *  a boolean fails to compile instead of quietly reading as NaN. */
export type CoordinateInput = number | string | null | undefined;

/** Coerces a form-control value to a coordinate, or NaN when it carries no number
 *  at all. Number() alone is not enough: Number(null), Number('') and Number('  ')
 *  are all 0, which reads an unset control as a point on the equator. */
function toCoordinate(value: CoordinateInput): number {
  if (value === null || value === undefined) {
    return NaN;
  }
  if (typeof value === 'string' && value.trim() === '') {
    return NaN;
  }
  const parsed = Number(value);
  return isFinite(parsed) ? parsed : NaN;
}

/** True when latitude and longitude together describe a real position on Earth.
 *  Rejects null, undefined, '', NaN, Infinity, and the two pairs this codebase
 *  uses to mean "not set": (0, 0) and the (-1, -1) marker that Register-new-Store
 *  seeds the form with (store-create.component.ts). Both are real coordinates —
 *  open ocean off West Africa — so nothing but an explicit rule can reject them.
 *  Latitude 0 with a real longitude (or the reverse) is a valid point. */
export function hasPinLocation(
  latitude: CoordinateInput,
  longitude: CoordinateInput,
): boolean {
  const lat = toCoordinate(latitude);
  const lng = toCoordinate(longitude);
  if (isNaN(lat) || isNaN(lng)) {
    return false;
  }
  if (lat === 0 && lng === 0) {
    return false;
  }
  return !(lat === -1 && lng === -1);
}

/** Writes a dragged-pin position into the form without touching address text fields.
 *  Clears placeId and addressSource when those controls exist, marks the form dirty. */
export function applyPinLocationToForm(
  form: FormGroup,
  location: MapPinLocation,
): void {
  const patch: Record<string, any> = {
    latitude: location.lat,
    longitude: location.lng,
  };
  if (form.contains('placeId')) {
    patch['placeId'] = null;
  }
  if (form.contains('addressSource')) {
    patch['addressSource'] = null;
  }
  form.patchValue(patch);
  form.markAsDirty();
  form.updateValueAndValidity();
}
